import { ChangeDetectionStrategy, Component } from '@angular/core';
import { TableCellComponent } from '@vsolv/vectors/table';
import { Observable } from 'rxjs';
import { DISTRIBUTOR_TABLE_CONFIG } from '../../config/distributor-table.config';

export interface DistributorNameCellState {
  expandable: boolean;
  expanded: boolean;
  loading: boolean;
}

export interface DistributorNameCellProps {
  text: string;
  classes?: string;

  /** 0 for top-level rows. */
  level: number;
  isSub: boolean;
  /** Shown on the badge when known. */
  parentName?: string | null;

  /** Props are built once per row, so live state comes in as a stream. Null hides the toggle. */
  state$: Observable<DistributorNameCellState> | null;

  toggled(): void;
}

// Default CD — `data` resolves async, an OnPush cell would render empty
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  template: `
    <div *ngIf="data" class="flex items-center gap-2 w-full">
      <!-- -my-2 cancels the cell padding so rails connect across rows -->
      <span *ngIf="rails.length" class="flex self-stretch -my-2 shrink-0">
        <span *ngFor="let rail of rails" class="w-6 border-l-2 border-gray-300"></span>
      </span>

      <ng-container *ngIf="data.state$ | async as state">
        <button
          *ngIf="state.expandable; else spacer"
          vs-icon-button
          type="button"
          size="sm"
          appearance="clear"
          class="shrink-0 !h-8 !w-8 !p-0"
          [attr.aria-label]="state.expanded ? config.collapseRow : config.expandRow"
          [disabled]="state.loading"
          (click)="$event.stopPropagation(); data.toggled()"
        >
          <vs-icon [name]="state.expanded ? 'chevron-down' : 'chevron-right'" class="h-5 w-5 text-gray-600"></vs-icon>
        </button>

        <ng-template #spacer>
          <!-- same size as the toggle so rows line up -->
          <span class="shrink-0 w-8 h-8"></span>
        </ng-template>
      </ng-container>

      <p class="grow self-center text-sm text-gray-600" [class]="data.classes ?? ''">{{ data.text }}</p>

      <vs-badge
        *ngIf="data.isSub"
        theme="default"
        class="shrink-0 max-w-[180px] overflow-hidden"
        tooltipStyle="light"
        [vsTooltip]="badgeText"
      >
        <span class="block truncate">{{ badgeText }}</span>
      </vs-badge>
    </div>
  `,
})
export class DistributorNameCellComponent extends TableCellComponent<DistributorNameCellProps> {
  protected readonly config = DISTRIBUTOR_TABLE_CONFIG;

  get rails(): unknown[] {
    return Array.from({ length: this.data?.level ?? 0 });
  }

  get badgeText(): string {
    return this.data?.parentName
      ? `${this.config.subBadgePrefix} ${this.data.parentName}`
      : this.config.subDistributorBadge;
  }
}
